import React, { FC, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import { Alert, AlertTitle, Stack } from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";

import Header from "../../components/Header";
import { LoadingError } from "../../components/LoadingError";
import NamespaceRoleGuard from "../../components/NamespaceRoleGuard";
import Page from "../../components/Page";
import SaveBackdrop from "../../components/SaveBackdrop";
import StickyEditBar from "../../components/StickyEditBar";
import {
  ActivityIcon,
  ClockIcon,
  ClusterIcon,
  DeploymentHistoryIcon,
  GlobalIcon,
  HeadersIcon,
  LoadBalancerIcon,
  MCPIcon,
  OutlinedSettingsIcon,
  PersonIcon,
  SearchIcon,
} from "../../components/icons";
import useFeatureFlag from "../../hooks/use-feature-flag";
import useReadOnly from "../../hooks/use-readonly";
import { clearForm, getSettingsFormProperty } from "../../slices/settings";
import { useDispatch, useSelector } from "../../store";
import SettingsSubNav, {
  SettingsSubNavMobileDropdown,
} from "./components/SettingsSubNav";
import { Advanced } from "./components/sections/Advanced";
import { ClusterInfo } from "./components/sections/ClusterInfo";
import Dns from "./components/sections/Dns";
import { Domains } from "./components/sections/Domains";
import { General } from "./components/sections/General";
import { Headers } from "./components/sections/Headers";
import IdentityProvider from "./components/sections/IdentityProvider";
import { Logging } from "./components/sections/Logging";
import Mcp from "./components/sections/Mcp";
import Ssh from "./components/sections/Ssh";
import { Timeouts } from "./components/sections/Timeouts";
import { Tracing } from "./components/sections/Tracing";
import { useSettingsFunctions } from "./components/useSettingsFunctions";
import {
  SettingsSubNavItem,
  useSettingsSubNavState,
} from "./components/useSettingsSubNavState";
import { setClusterAndOrg } from "@/slices/current";

const Edit: FC<React.PropsWithChildren> = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { organizationId, clusterId } = useSelector((state) => state.current);
  const oid = searchParams.get("oid");
  const cid = searchParams.get("cid");
  const originatorId = useSelector(
    getSettingsFormProperty("originatorId", ""),
  ) as string;

  useEffect(() => {
    if (oid && cid) {
      dispatch(
        setClusterAndOrg({
          organizationId: oid,
          clusterId: cid,
        }),
      );
    }
  }, [oid, cid, dispatch]);

  const readonly = useReadOnly({ clusterId, organizationId });
  const {
    isUpdating,
    settingsHash,
    shouldSave,
    allowCancel,
    clusterRefresh,
    clusterError,
    settingsError,
    refresh,
    handleClickDiscard,
    handleSubmit,
    cluster,
  } = useSettingsFunctions();
  const tracingEnabled = useFeatureFlag("otlp-tracing", false);

  useEffect((): (() => void) => {
    return (): void => {
      dispatch(clearForm());
    };
  }, []);

  const subNavItems: SettingsSubNavItem[] = [
    { id: "general", label: "General", icon: <OutlinedSettingsIcon /> },
    { id: "domains", label: "Domains", icon: <GlobalIcon /> },
    {
      id: "identity-provider",
      label: "Identity Provider",
      icon: <PersonIcon />,
    },
    { id: "headers", label: "Headers", icon: <HeadersIcon /> },
    { id: "timeouts", label: "Timeouts", icon: <ClockIcon /> },
    { id: "logging", label: "Logging", icon: <DeploymentHistoryIcon /> },
    ...(tracingEnabled
      ? [{ id: "tracing", label: "Tracing", icon: <ActivityIcon /> }]
      : []),
    { id: "dns", label: "DNS", icon: <SearchIcon /> },
    { id: "ssh", label: "SSH", icon: <ClusterIcon /> },
    { id: "mcp", label: "MCP", icon: <MCPIcon /> },
    { id: "advanced", label: "Advanced", icon: <LoadBalancerIcon /> },
  ];

  const { activeId, handleSelect } = useSettingsSubNavState(subNavItems);

  if (!clusterId) {
    return (
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        sx={{ width: "75%", mt: "10%" }}
      >
        <Alert severity={"info"} sx={{ m: "auto" }}>
          <AlertTitle>No Cluster Selected!</AlertTitle>
          Select a Cluster from the Top Navigation to Edit its settings.
        </Alert>
      </Grid>
    );
  }

  if (!settingsHash || !cluster?.flavor) {
    return null;
  }

  if (clusterError || settingsError) {
    return (
      <Page title="Cluster Settings">
        <Header title="Editing Cluster Settings" breadcrumbs={true} />
        <LoadingError />
      </Page>
    );
  }

  const isHosted = cluster?.flavor === "hosted";

  return (
    <Page title="Cluster Settings">
      <SaveBackdrop saving={isUpdating}>Settings Saved</SaveBackdrop>
      <Header
        breadcrumbs={true}
        title="Editing Cluster Settings"
        originatorId={originatorId}
      />
      <NamespaceRoleGuard namespaceId={clusterId} role={"viewer"} warn={true}>
        <form autoComplete="off" onSubmit={handleSubmit}>
          <Stack spacing={4}>
            <ClusterInfo clusterId={cluster.id} />
            {isHosted ? (
              <Domains
                isHosted
                refresh={refresh + clusterRefresh}
                readonly={readonly}
                tabValue={"domains"}
              />
            ) : (
              <>
                <SettingsSubNavMobileDropdown
                  items={subNavItems}
                  activeId={activeId}
                  onSelect={handleSelect}
                />
                <Grid container spacing={3}>
                  <Grid
                    sx={{ display: { xs: "none", md: "block" } }}
                    size={{
                      xs: false,
                      md: "auto",
                    }}
                  >
                    <SettingsSubNav
                      items={subNavItems}
                      activeId={activeId}
                      onSelect={handleSelect}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: "grow",
                    }}
                  >
                    <Stack spacing={3}>
                      <General
                        refresh={refresh + clusterRefresh}
                        readonly={readonly}
                        tabValue={"general"}
                      />
                      <Domains
                        isHosted={false}
                        refresh={refresh + clusterRefresh}
                        readonly={readonly}
                        tabValue={"domains"}
                      />
                      <IdentityProvider
                        refresh={refresh}
                        readonly={readonly}
                        tabValue={"identity-provider"}
                      />
                      <Headers
                        refresh={refresh}
                        readonly={readonly}
                        tabValue={"headers"}
                      />
                      <Timeouts
                        refresh={refresh}
                        readonly={readonly}
                        tabValue={"timeouts"}
                      />
                      <Logging
                        refresh={refresh}
                        readonly={readonly}
                        tabValue={"logging"}
                      />
                      {tracingEnabled && (
                        <Tracing
                          refresh={refresh}
                          readonly={readonly}
                          tabValue={"tracing"}
                        />
                      )}
                      <Dns
                        refresh={refresh}
                        readonly={readonly}
                        tabValue="dns"
                      />
                      <Ssh
                        refresh={refresh}
                        readonly={readonly}
                        tabValue="ssh"
                      />
                      <Mcp
                        refresh={refresh}
                        readonly={readonly}
                        tabValue="mcp"
                      />
                      <Advanced
                        refresh={refresh}
                        readonly={readonly}
                        tabValue={"advanced"}
                      />
                    </Stack>
                  </Grid>
                </Grid>
              </>
            )}
          </Stack>
          {!isHosted && (
            <>
              <Box m={2} pt={3}>
                <span />
              </Box>
              <StickyEditBar
                name="Settings"
                cancelType="Discard"
                allowCancel={allowCancel}
                allowSave={shouldSave()}
                onCancel={handleClickDiscard}
                onSave={handleSubmit}
              />
            </>
          )}
        </form>
      </NamespaceRoleGuard>
    </Page>
  );
};

export default Edit;
